import utils, {axiosWithAuth} from '@imt/vue-toolbox/src/utils';
import keyBy from 'lodash/keyBy';
import mapValues from 'lodash/mapValues';
import sortBy from 'lodash/sortBy';
import Vue from 'vue';
import Vuex from 'vuex';

Vue.use(Vuex);

export const authedAxios = axiosWithAuth();

export const getters = {
    actions(state, storeGetters) {
        return Object.entries(state.availableOperations).map(([key, operation]) => {
            state.availableOperations[key].helpWording = storeGetters._objectifyHelpWording(storeGetters._parseHelpWording(state.availableOperations[key].description));
            state.availableOperations[key].description = storeGetters._parseDescription(state.availableOperations[key].description)[0];

            return {
                ...operation,
            };
        });
    },
    _objectifyHelpWording: () => helpWording => {
        return mapValues(keyBy(helpWording, hw => {
            return hw.key;
        }), 'wording');
    },
    _parseDescription: () => description => {
        return description?.match(new RegExp(/^(([\w\W]+)\n\n)|[\w\W]+/, 'g'))?.map(m => m.trim()) || [''];
    },
    _parseHelpWording: () => description => {
        return description?.match(new RegExp(/(:\w+:\W+.+)\n/, 'g'))?.map(hw => {
            return {
                key: hw.split(': ')[0].substring(1),
                wording: hw.split(': ')[1].trim(),
            };
        }) || [];
    },
};

export const actions = {
    async runDatamap(context, {code, object, versionId, policySystemId}) {
        const data = {
            type: 'Result',
            code,
            object,
            versionId,
            policySystemId,
        };

        let response = await authedAxios.post(
            `results/`,
            utils.dataFormatter.serialize({stuff: data}),
        );

        return utils.dataFormatter.deserialize(response.data);
    },
    async deleteOperation(context, operationId) {
        return await authedAxios.delete(`/operations/${operationId}/`);
    },
    async fetchOperations(context) {
        let response = await authedAxios.get(`/available-operations/`);

        context.commit('SET_DATA', {
            type: 'availableOperations',
            data: response.data.data,
        });

        return response;
    },
    async fetchDataMap(context, id, queryParams = '') {
        const response = await authedAxios.get(`/datamaps/${id}/?${queryParams}`),
            dataMap = utils.dataFormatter.deserialize(response.data);

        context.commit('SET_DATA', {
            type: 'dataMap',
            data: dataMap,
        });

        return dataMap;
    },
    async fetchVersion(context, id, queryParams = '') {
        if (!queryParams.length) {
            queryParams = `include=operations,updated_by`;
        }

        const response = await authedAxios.get(`/versions/${id}/?${queryParams}`),
            version = utils.dataFormatter.deserialize(response.data);

        context.commit('SET_DATA', {
            type: 'version',
            data: version,
        });

        return version;
    },
    async saveDataMapOperations(context) {
        context.commit('SET_DATA', {
            type: 'saving',
            data: true,
        });

        await Promise.all(
            context.state.version.operations.map((operation, index) =>
                context.dispatch('saveOperation', {
                    ...operation,
                    versionId: context.state.version.id,
                    order: index,
                })
            )
        );

        context.commit('SET_DATA', {
            type: 'hasChanges',
            data: false,
        });
    },
    async saveOperation(context, operation, queryParams = '') {
        let method = 'patch';

        context.commit('SET_DATA', {
            type: 'saving',
            data: true,
        });

        if (!operation.id) {
            method = 'post';
            operation.type = 'Operation';
            operation.versionId = context.state.version.id;
        }

        const response = await authedAxios[method](
            `/operations/${operation.id ? operation.id + '/' : ''}?${queryParams}`,
            utils.dataFormatter.serialize({
                stuff: operation,
            })
        );

        return utils.dataFormatter.deserialize(response.data);
    },
    async saveVersion(context) {
        context.commit('SET_DATA', {
            type: 'saving',
            data: true,
        });

        const response = await authedAxios.patch(
                `/versions/${context.state.version.id}/?include=operations,updated_by`,
                utils.dataFormatter.serialize({
                    stuff: {
                        ...context.state.version,
                        type: 'Version',
                        id: `${context.state.version.id}`,
                        relationshipNames: ['dataMap', 'draftOf', 'createdBy', 'updatedBy'],
                    }
                }),
            ),
            updatedVersion = utils.dataFormatter.deserialize(response.data);

        context.commit('SET_DATA', {
            type: 'version',
            data: updatedVersion,
        });

        context.commit('SET_DATA', {
            type: 'hasChanges',
            data: false,
        });

        context.commit('SET_DATA', {
            type: 'saving',
            data: false,
        });

        return updatedVersion;
    },
    updateOperations(context, operations) {
        context.commit('SET_VERSION_OPERATIONS', operations);
        context.commit('SET_DATA', {
            type: 'hasChanges',
            data: true,
        });
    },
};

export const mutations = {
    REMOVE_OPERATION(state, operationId) {
        const removeOperationAtIndex = state.version.operations.findIndex((o) => o.id === operationId);
        state.version.operations.splice(removeOperationAtIndex, 1);
    },
    REMOVE_OPERATION_AT_INDEX(state, index) {
        state.version.operations.splice(index, 1);
    },
    SET_DATA(state, {type, data}) {
        state[type] = data;
    },
    SET_FILTER(state, value) {
        state.filter = value;
    },
    SET_VERSION_OPERATIONS(state, operations) {
        state.version.operations = sortBy(operations, 'order');
    },
};

export const state = () => {
    return {
        dataMapRunning: false,
        availableOperations: {},
        filter: '',
        hasChanges: false,
        saving: false,
        version: {},
        dataMap: {},
    };
};

export default {
    namespaced: true,
    state: state(),
    actions,
    getters,
    mutations,
};
